import { Button } from "@bandwith/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useSession } from "../lib/auth";
import { useSignInRedirect } from "../lib/useSignInRedirect";

type AuthLinkButtonProps = {
  to: string;
  params?: Record<string, string>;
  children: ReactNode;
  variant?:
    | "secondary"
    | "default"
    | "destructive"
    | "destructive-outline"
    | "ghost";
};

/**
 * A button-styled link that requires authentication to navigate.
 * When clicked by an unauthenticated user:
 *   - If test auth is enabled, redirects to sign-in page
 *   - If test auth is disabled, triggers Google OAuth directly
 * When clicked by an authenticated user, navigates normally.
 */
export function AuthLinkButton({
  to,
  params,
  children,
  variant = "secondary",
}: AuthLinkButtonProps) {
  const { session } = useSession();
  const signInRedirect = useSignInRedirect();
  const isSignedIn = !!session?.user;

  const handleClick = (e: React.MouseEvent) => {
    if (!isSignedIn) {
      e.preventDefault();
      signInRedirect(to);
    }
  };

  return (
    <Button asChild variant={variant}>
      <Link to={to} params={params} onClick={handleClick}>
        {children}
      </Link>
    </Button>
  );
}
